import React, { useRef, useCallback, useMemo } from 'react';
import cn from 'classnames';
import { ListChildComponentProps, VariableSizeList } from "react-window";
import Spinner from '@dealroadshow/uikit/core/components/Loader/Spinner';
import CatalogNavigation from './CatalogNavigation';
import CatalogItem from './CatalogItem';
import ICatalogCollection from '@/finsight/application/Catalog/common';
import {
  VirtualListContextProvider ,useVirtualListContext,
} from '@dealroadshow/uikit/core/components/Table/VirtualDataTable';
import CatalogListOuterComponent from "./CatalogListOuterComponent";

import styles from './catalogList.scss';

interface IProps {
  isFetching: boolean,
  data: ICatalogCollection,
  catalogUrl: string,
}

const InnerComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>(({
  children,
  style,
  ...otherProps
}, ref) => (
  <div { ...otherProps } ref={ ref } style={ style }>
    <div style={ {
      position: 'relative',
      width: '100%',
    } }>
      { children }
    </div>
  </div>
));

const CatalogList = ({
  isFetching,
  data,
  catalogUrl,
}: IProps) => {
  const listRef = useRef(null);
  const catalogRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>();
  const listData = Object.entries(data)
    .map(([key, value]) => ({ [key]: value }));
  const {
    getSize,
    handleRenderItems,
  } = useVirtualListContext();

  const labels = useMemo(() => Object.keys(data), [data]);
  const hasResults = !!labels.length;

  const onNavigationClickHandler = useCallback((index: number) => {
    listRef.current.scrollToItem(index, 'start');
    return false;
  }, []);

  const VirtualListComponent = useCallback((props: ListChildComponentProps) => (
    <CatalogItem
      listRef={ listRef }
      { ...props }
    />
  ), [listRef]);

  if (isFetching) {
    return (
      <div className={ cn(styles.container, { [styles.emptyContainer]: isFetching }) }>
        <Spinner />
      </div>
    );
  }

  if (!hasResults) {
    return null;
  }

  return (
    <>
      <CatalogNavigation
        onClickHandler={ onNavigationClickHandler }
        navigationFilter={ labels }
      />
      <div className={ styles.container } ref={ catalogRef }>
        <VariableSizeList
          itemData={ {
            items: listData,
            catalogUrl,
          } }
          ref={ listRef }
          innerRef={ innerRef }
          itemCount={ listData.length }
          itemSize={ getSize }
          outerElementType={ CatalogListOuterComponent }
          innerElementType={ InnerComponent }
          height={ listRef?.current?.scrollHeight || 600 }
          onItemsRendered={ (props) => handleRenderItems(innerRef, listRef, props) }
          overscanCount={ 2 }
          outerRef={ outerRef }
          onScroll={ (prop) => window.scrollTo({
            top: prop.scrollOffset + outerRef.current.getBoundingClientRect().top + window.scrollY,
          })
          }
          width="100%">
          { VirtualListComponent }
        </VariableSizeList>
      </div>
    </>
  );
};

export default (props) => (
  <VirtualListContextProvider { ...props }>
    <CatalogList { ...props } />
  </VirtualListContextProvider>
);
